import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const SalesAgentLoginForm = () => {
  const navigate = useNavigate();
  const { signIn, userProfile, profileLoading } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberDevice: false,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);

  // Redirect based on user type after profile loads
  useEffect(() => {
    if (loginSuccess && !profileLoading && userProfile) {
      const userType = userProfile?.user_type;
      
      if (userType === 'sales_agent') {
        navigate('/sales-agent-dashboard');
      } else if (userType === 'superadmin') {
        navigate('/crm-dashboard-overview');
      } else {
        setErrors({ submit: 'Deze login is alleen voor sales agents. Gebruik de juiste login pagina.' });
        setLoginSuccess(false);
        setLoading(false);
      }
    }
  }, [loginSuccess, profileLoading, userProfile, navigate]);

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors?.[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.email) {
      newErrors.email = 'Email is verplicht';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Ongeldig email formaat';
    }
    if (!formData?.password) {
      newErrors.password = 'Wachtwoord is verplicht';
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const newErrors = validateForm();
    if (Object.keys(newErrors)?.length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    setErrors({});
    setLoginSuccess(false);

    try {
      const { error } = await signIn(formData?.email, formData?.password);
      if (error) {
        setErrors({ submit: error?.message || 'Inloggen mislukt. Controleer uw gegevens.' });
        setLoading(false);
      } else {
        setLoginSuccess(true);
      }
    } catch (error) {
      setErrors({ submit: 'Er is een fout opgetreden. Probeer het opnieuw.' });
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-primary rounded-lg flex items-center justify-center mx-auto mb-4">
          <Icon name="Briefcase" size={32} color="white" />
        </div>
        <h1 className="text-3xl font-heading font-bold text-foreground mb-2">Sales Agent Login</h1>
        <p className="text-muted-foreground">Log in om uw territorium te beheren</p>
      </div>

      {errors?.submit && (
        <div className="mb-4 p-4 bg-error/10 border border-error/20 rounded-lg">
          <p className="text-sm text-error">{errors?.submit}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Agent Email"
          type="email"
          placeholder="agent@verduurzaamwijzer.nu"
          value={formData?.email}
          onChange={(e) => handleInputChange('email', e?.target?.value)}
          error={errors?.email}
          required
        />

        <div className="relative">
          <Input
            label="Wachtwoord"
            type={showPassword ? 'text' : 'password'}
            placeholder="••••••••"
            value={formData?.password}
            onChange={(e) => handleInputChange('password', e?.target?.value)}
            error={errors?.password}
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-9 text-muted-foreground hover:text-foreground"
          >
            <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
          </button>
        </div>

        <div className="flex items-center justify-between">
          <label className="flex items-center space-x-2">
            <input 
              type="checkbox" 
              className="rounded border-input"
              checked={formData?.rememberDevice}
              onChange={(e) => handleInputChange('rememberDevice', e?.target?.checked)}
            />
            <span className="text-sm text-muted-foreground">Onthoud apparaat</span>
          </label>
          <Link to="/forgot-password" className="text-sm text-primary hover:underline">
            Wachtwoord vergeten?
          </Link>
        </div>

        <Button type="submit" fullWidth loading={loading}>
          Inloggen als Agent
        </Button>
      </form>

      <div className="mt-6 space-y-3">
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-background text-muted-foreground">Andere opties</span>
          </div>
        </div>

        <div className="flex flex-col space-y-2 text-center text-sm">
          <Link to="/sales-agent-registration" className="text-primary hover:underline font-medium">
            Registreer als Sales Agent
          </Link>
          <Link to="/leverancier-login" className="text-muted-foreground hover:text-foreground">
            Territorium verificatie
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SalesAgentLoginForm;